import * as React from 'react';
import { CheckCircle, AlertTriangle, XCircle, Info, X } from 'lucide-react';
import { cn } from '@/lib/utils';

/* ─── Types ────────────────────────────────────────────────────────────────── */

type ToastType = 'default' | 'success' | 'warning' | 'error';

interface Toast {
  id: string;
  message: string;
  type: ToastType;
  duration: number;
}

interface ToastContextValue {
  toast: (params: { message: string; type?: ToastType; duration?: number }) => void;
  dismiss: (id: string) => void;
}

/* ─── Context ──────────────────────────────────────────────────────────────── */

const ToastContext = React.createContext<ToastContextValue | null>(null);

/* ─── Hook ─────────────────────────────────────────────────────────────────── */

function useToast(): ToastContextValue {
  const ctx = React.useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within a <ToastProvider>');
  }
  return ctx;
}

/* ─── Icon map ─────────────────────────────────────────────────────────────── */

const iconMap: Record<ToastType, React.ElementType> = {
  default: Info,
  success: CheckCircle,
  warning: AlertTriangle,
  error: XCircle,
};

const typeStyles: Record<ToastType, string> = {
  default: 'border-raios-border text-raios-text',
  success: 'border-raios-success/40 text-raios-success-text',
  warning: 'border-raios-warning/40 text-raios-warning-text',
  error: 'border-raios-danger/40 text-raios-danger-text',
};

/* ─── Single Toast ─────────────────────────────────────────────────────────── */

interface ToastItemProps {
  toast: Toast;
  onDismiss: (id: string) => void;
}

function ToastItem({ toast, onDismiss }: ToastItemProps) {
  const [visible, setVisible] = React.useState(false);
  const [exiting, setExiting] = React.useState(false);
  const Icon = iconMap[toast.type];

  React.useEffect(() => {
    // Trigger enter animation on next frame
    const raf = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setExiting(true);
      setTimeout(() => onDismiss(toast.id), 300);
    }, toast.duration);

    return () => clearTimeout(timer);
  }, [toast.duration, toast.id, onDismiss]);

  const handleDismiss = () => {
    setExiting(true);
    setTimeout(() => onDismiss(toast.id), 300);
  };

  return (
    <div
      role="alert"
      className={cn(
        'flex items-start gap-3 w-80 bg-raios-elevated border rounded-lg px-4 py-3 shadow-raios-lg transition-all duration-300 ease-out',
        typeStyles[toast.type],
        visible && !exiting
          ? 'translate-x-0 opacity-100'
          : 'translate-x-full opacity-0'
      )}
    >
      <Icon className="h-5 w-5 shrink-0 mt-0.5" />
      <p className="flex-1 text-sm text-raios-text">{toast.message}</p>
      <button
        type="button"
        onClick={handleDismiss}
        className="shrink-0 text-raios-muted hover:text-raios-text transition-colors cursor-pointer"
        aria-label="Dismiss"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

/* ─── Provider ─────────────────────────────────────────────────────────────── */

interface ToastProviderProps {
  children: React.ReactNode;
}

function ToastProvider({ children }: ToastProviderProps) {
  const [toasts, setToasts] = React.useState<Toast[]>([]);

  const dismiss = React.useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toast = React.useCallback(
    ({
      message,
      type = 'default',
      duration,
    }: {
      message: string;
      type?: ToastType;
      duration?: number;
    }) => {
      const defaultDuration = type === 'error' ? 7000 : 5000;
      const newToast: Toast = {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
        message,
        type,
        duration: duration ?? defaultDuration,
      };
      setToasts((prev) => [...prev, newToast]);
    },
    []
  );

  const contextValue = React.useMemo(() => ({ toast, dismiss }), [toast, dismiss]);

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <div className="fixed bottom-4 right-4 z-[500] flex flex-col gap-2">
        {toasts.map((t) => (
          <ToastItem key={t.id} toast={t} onDismiss={dismiss} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export { ToastProvider, useToast };
export type { ToastType, Toast };
